import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getRunningBill, addCharge, addItemCharge, reverseCharge, grantConcession } from '../../../services/api/admissionChargesApi';
import { collectDeposit, refundDeposit, openAdmissionReceiptPdf } from '../../../services/api/admissionPaymentsApi';
import { getFinalBill, openDischargeSummaryPdf, openInterimInvoicePdf, issueTpaBill, openTpaBillPdf } from '../../../services/api/admissionDischargeApi';
import { usePermission, useScope } from '../../../context/PermissionContext';
import useOrgSettingsStore from '../../../stores/orgSettingsStore';
import {
  CHARGE_KIND,
  CHARGE_FILTER_ALL,
  CHARGE_FILTER_CHIPS,
  CHARGE_PERMISSIONS,
  PAYMENT_PERMISSIONS,
  DISCHARGE_TYPE_LABELS,
  DISCHARGE_PERMISSIONS,
  DISCHARGE_ERROR_CODE,
  PAYER_TYPE_LABELS,
  canIssueTpaBill,
  tpaBillBlockedReason,
  isDepositCollectable,
  daysAdmitted,
  isAwaitingBed,
  isTerminal,
  hasFinalBill,
  isDischargeStartable,
  isInDischargeFlow,
  isDeathOutcome,
  ADMISSION_ACCESS_ERROR_CODE,
} from '../../../constants/admission';
import { formatPaise, formatIsoDateOnly, todayYmd } from '../../../utils/money';
import useDiagnosticsBillPrint from '../../../hooks/useDiagnosticsBillPrint';
import { diagnosticsBillLabel } from '../../../hooks/useDiagnosticsBillPrint';
import { buildBillModel, describeBalance, buildFinalBillModel, buildDuesAddendumModel, buildDiagWaiverLine, buildRunningInsuranceModel, tpaBillErrorMessage } from './billModel';
// S4 (FE-4, Screen C) — the ONE "Planned discharge: …" chip formatter (FE-2),
// reused verbatim so the header chip can never drift from the desk row chip
// (FE-3) or the Complete-step confirm line (FE-4/dischargeModel).
import { plannedDischargeChipText } from '../discharge/planDischargeModel';
import { DIAG_WAIVER_COPY as DWC, formatDiagWaiverCopy as fmtDiagCopy } from '../../../constants/diagWaiverCopy';
import AddChargeDialog from './AddChargeDialog';
import ReverseChargeDialog from './ReverseChargeDialog';
import DepositsSection from './DepositsSection';
import TakeDepositDialog from './TakeDepositDialog';
import RefundDialog from './RefundDialog';
import WaiverConcessionDialog from './WaiverConcessionDialog';
import DiagnosticWaiverDialog from './DiagnosticWaiverDialog';
import { withEmphasis } from '../../../utils/richCopy';
import AccrualRunBanner from '../AccrualRunBanner';
import NoAccessPanel, { NO_ACCESS_VARIANT } from '../../common/NoAccessPanel';

// L3 mount point (day-1 ADDENDUM-3) — the labcredit-fe lane owns the real
// component at this path/export; lazy so a heavier real implementation never
// bloats this bundle for orgs that never mount it (COLLECT_PAYMENT-gated).
const SettleCreditsCard = React.lazy(() => import('../credit/SettleCreditsCard'));

/**
 * RunningBillPanel (C3 · S6, Variant A "grouped by day") — the itemized running
 * bill for an admitted patient, mounted inside the AdmissionRow expanded detail.
 *
 * Distinct from the pre-admit "Estimate — not a bill": this is a live bill whose
 * balance is the server's cached `balance_paise` (single source — never
 * re-derived, never a fake ₹0). Charges group by day (newest first), collapse
 * beyond the latest N days, and filter/search over the shared `buildBillModel`
 * selector. Corrections are reversing entries (struck-through original + linked
 * red minus line) — POSTED lines are immutable.
 *
 * Runtime safety: the bill is fetched ONCE per mount (ref-guarded single-flight);
 * a re-render can't multiply the request. A successful add/reverse triggers one
 * bounded reload.
 */
const chip = 'inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap';
const chipMuted = `${chip} border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300`;
const chipTeal = `${chip} border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200`;
const chipAmber = `${chip} border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200`;

// S5 AC4 — same-business-day self-serve window. `created_at` is already
// org-local (IST) on the wire (existing convention — never a hand-rolled
// `toISOString().slice(0,10)` UTC slice); ADMIN bypasses entirely (checked
// by the caller via `isAdmin`), so this only ever gates a non-admin.
function isPastOrgDay(createdAtIso) {
  return typeof createdAtIso === 'string' && createdAtIso.length >= 10 && createdAtIso.slice(0, 10) !== todayYmd();
}

function LineRow({ line, canReverse, isAdmin, onReverse }) {
  const negative = (Number(line.amount_paise) || 0) < 0;
  // REVERSAL rows: the BE description carries the raw charge_id ("Reversal of
  // chg_…") — show the friendly linked label instead (contract untouched).
  const title = line.isReversal && line.reversalOfLabel
    ? `Reversal — ${line.reversalOfLabel}`
    : line.description;
  let subtitle = null;
  if (line.kind === CHARGE_KIND.BED_DAY) subtitle = 'Posted automatically at midnight';
  else if (line.kind === CHARGE_KIND.REVERSAL) {
    const who = line.entered_by?.name;
    subtitle = `Reverses the ${line.reversalOfLabel || 'charge'}${line.reason ? ` · “${line.reason}”` : ''}${who ? ` · by ${who}` : ''}`;
  } else if (line.kind === CHARGE_KIND.CONCESSION) {
    const who = line.entered_by?.name;
    subtitle = `Waiver / concession${line.reason ? ` · “${line.reason}”` : ''}${who ? ` · by ${who}` : ''}`;
  } else subtitle = line.entered_by?.name ? `Added by ${line.entered_by.name}` : 'Manual charge';

  const showReverse = canReverse && !line.isReversal && !line.reversed;
  // Receptionist (non-admin) loses the control past its posting day — ADMIN
  // reverses any day (contract §2.5 bypass). Disabled-with-reason, never
  // hidden, so the rule is visible rather than silently absent.
  const reversePastDay = showReverse && !isAdmin && isPastOrgDay(line.created_at);
  const isConcession = line.kind === CHARGE_KIND.CONCESSION;

  return (
    <div
      className={`flex items-center gap-2.5 px-4 py-2 text-[13px] ${line.isReversal ? 'border-l-[3px] border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20' : isConcession ? 'border-l-[3px] border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}
      data-testid={`ledger-line-${line.charge_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${line.reversed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-white'}`}>
          {title}
          {line.reversed && (
            <span className={`${chip} ml-2 border-red-300 dark:border-red-700 text-red-600 dark:text-red-300 no-underline`}>
              Reversed{line.reversedOnLabel ? ` on ${line.reversedOnLabel}` : ''}
            </span>
          )}
        </div>
        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</div>
      </div>
      <span className="w-28 text-right text-[12px] text-slate-500 dark:text-slate-400 flex-none hidden sm:block">{line.qty} × {formatPaise(line.unit_price_paise)}</span>
      <span className="w-12 text-right text-[12px] text-slate-500 dark:text-slate-400 flex-none hidden sm:block">{Number(line.gst_rate) > 0 ? `${line.gst_rate}%` : '—'}</span>
      <span className={`w-24 text-right font-semibold tabular-nums flex-none ${negative ? 'text-red-600 dark:text-red-400' : line.reversed ? 'line-through text-slate-400 dark:text-slate-500 font-medium' : 'text-slate-900 dark:text-white'}`}>{formatPaise(line.amount_paise)}</span>
      <span className="w-7 flex-none text-center">
        {showReverse && (
          <button
            type="button"
            onClick={() => onReverse(line)}
            disabled={reversePastDay}
            className="w-6 h-6 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 leading-none disabled:opacity-60 disabled:cursor-not-allowed"
            title={reversePastDay ? 'Charges from an earlier day can only be reversed by an admin.' : 'Reverse charge'}
            aria-label={`Reverse ${line.description}`}
            data-testid={`ledger-reverse-${line.charge_id}`}
          >⋮</button>
        )}
      </span>
    </div>
  );
}

/**
 * DiagWaiverBillLine (DIAG-DISCOUNT v5 FE-G, copy §13.10) — the ONE render of
 * the recorded-diagnostic-waiver line, shared verbatim by the running bill
 * AND the final bill so the two surfaces can never disagree. Reads the
 * `buildDiagWaiverLine` selector's output only — never re-derives the
 * status/attribution itself. Renders NOTHING when nothing is recorded
 * (`.none` — never a fabricated ₹0 row).
 */
function DiagWaiverBillLine({ line }) {
  if (!line.visible) return null;
  return (
    <div
      className="mx-4 mt-3 rounded-lg border border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 p-3 text-[12.5px]"
      data-testid="diag-waiver-bill-line"
    >
      <div className="font-bold text-indigo-900 dark:text-indigo-200">{DWC['diagWaiver.bill.recorded.title']}</div>
      <div className="text-slate-700 dark:text-slate-200 mt-0.5">
        {DWC['diagWaiver.bill.recorded.amount.label']}{' '}
        <b className="tabular-nums text-slate-900 dark:text-white">{formatPaise(line.recordedPaise)}</b>
      </div>
      <div className="text-slate-600 dark:text-slate-300 mt-1" data-testid="diag-waiver-bill-line-status">
        {line.status === 'pending' && withEmphasis(DWC['diagWaiver.bill.recorded.pending'])}
        {line.status === 'partly' && fmtDiagCopy('diagWaiver.bill.recorded.partly', {
          applied: formatPaise(line.appliedPaise), unused: formatPaise(line.remainingPaise),
        })}
        {line.status === 'fullyApplied' && DWC['diagWaiver.bill.recorded.fullyApplied']}
        {line.status === 'reversed' && fmtDiagCopy('diagWaiver.bill.recorded.reversed', {
          date: line.reversedAtLabel || '—', name: line.reversedByName || '—',
        })}
      </div>
      {/* Attribution is a money instruction — ALWAYS shown, never conditional. */}
      <div className="text-slate-500 dark:text-slate-400 mt-1" data-testid="diag-waiver-bill-line-attribution">
        {fmtDiagCopy('diagWaiver.bill.recorded.attribution', {
          name: line.recordedByName || '—', date: line.recordedAtLabel || '—',
        })}
      </div>
      {line.reason && (
        <div className="text-slate-500 dark:text-slate-400 mt-0.5" data-testid="diag-waiver-bill-line-reason">
          {fmtDiagCopy('diagWaiver.bill.recorded.reason', { reason: line.reason })}
        </div>
      )}
    </div>
  );
}

/**
 * useTpaBillIssue (TPA-PREAUTH/tpa-bill · S1 · FE-2; FIX-4-FE) — shared "TPA
 * Bill" issue-button state for BOTH bill views (RunningBillLive AND
 * FinalBillView) so the double-click guard + idempotency plumbing exists in
 * exactly one place, never a forked copy. Issuing NEVER reloads the
 * running/final bill and NEVER calls `listTpaBills` — the POST response
 * (`TpaBillIssueResponse`) already carries every field the caller needs to
 * show the new row locally; the persisted, full history lives on
 * `TpaBillSection` (Screen C, the admission's Details tab), which owns its
 * own single fetch. `issuingRef` (checked synchronously, before the first
 * `await`) is what makes a genuine double-click issue exactly once — the
 * SAME single-flight-guard idiom as `handlePrintInvoice`/`handleReprintInvoice`
 * above/below.
 *
 * FIX-4-FE (owner click-through finding) — one click now does the WHOLE job:
 * issue -> open the PDF for the returned bill -> (append the row) -> toast.
 * `reused` (additive C2 field, FIX-4-BE — absent on an older backend reads as
 * `false`) means the server found nothing changed since the last issue and
 * handed back the SAME bill rather than minting a new one: no row is
 * appended (there is nothing new to show), the toast reads differently, but
 * the PDF still opens — the click always ends with a document on screen.
 * `issuing` (and the disabled button it drives) stays true across BOTH the
 * issue POST and the PDF open, so the button can't be clicked again mid-flow.
 */
function useTpaBillIssue(admissionId) {
  const [issuedTpaBills, setIssuedTpaBills] = useState([]);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const issuingRef = useRef(false);

  const issue = useCallback(async () => {
    if (issuingRef.current) return;
    issuingRef.current = true;
    setIssuing(true);
    setError(null);
    setToast(null);
    const res = await issueTpaBill(admissionId);
    if (res.success) {
      const seqNo = res.data.seq_no;
      const reused = !!res.data.reused;
      // Reused: the bill already existed (nothing changed) — no new row to
      // append, only the existing one re-opened.
      if (!reused) setIssuedTpaBills((prev) => [res.data, ...prev]);
      const pdfRes = await openTpaBillPdf(admissionId, seqNo);
      if (!pdfRes.success) {
        // The issue itself succeeded (and any row it added stays) — only the
        // PDF open failed, so this is a plain, distinct error from an ISSUE
        // failure below.
        setError(tpaBillErrorMessage(pdfRes.code) || pdfRes.error || 'Could not open the TPA bill.');
      } else {
        setToast(reused
          ? `Nothing changed since bill –${seqNo}. Opened the existing bill.`
          : `TPA Bill –${seqNo} issued.`);
        setTimeout(() => setToast(null), 3000);
      }
    } else {
      // Contract reconcile: every C2 machine code gets plain desk copy; the
      // server `message` is never shown, and an unmapped code still degrades
      // to one readable fallback rather than a raw code.
      setError(tpaBillErrorMessage(res.code) || res.error || 'Could not issue the TPA bill.');
    }
    issuingRef.current = false;
    setIssuing(false);
  }, [admissionId]);

  return { issuedTpaBills, issuing, error, toast, issue };
}

/**
 * RunningBillPanel — dispatcher. A terminal admission's bill is FINAL and
 * locked (FinalBillView, C5 · FE-3); everything short of that is the live,
 * editable running bill (RunningBillLive, C3/C4). Two separate components
 * (rather than one branching inside a single body) so neither carries
 * conditional hooks — a mount only ever calls one hook set for its lifetime.
 *
 * `onDischarge` (C5 · FE-4) — optional callback forwarded to RunningBillLive's
 * header "Discharge…" button, the SAME dialog instance the desk row action
 * opens (mounted once, by the caller — never a second DischargeDialog here).
 */
// `embedded` (UX-1 Variant B) — the panel is mounted inside the workspace Bill
// tab, where the pinned KPI strip + status-driven task bar already own the
// owner-glance header AND the +Deposit / Waiver / Discharge / deposit-low
// actions. In embedded mode the panel therefore drops its OWN duplicate header,
// deposit-low banner, Waiver button and standalone deposit-collect — keeping
// ONLY the bill-line scope (reverse / refund / Interim Bill). "+ Add charge"
// also moved off this header in embedded mode (Round 3 Wave 3 relocation) — it
// now lives on the workspace task bar as the FIRST action, ahead of Order Lab
// and Add Deposit; the taskbar bumps `openAddChargeToken`, which this panel
// watches to open its OWN (single, unduplicated) AddChargeDialog instance.
// Rendered standalone (default `embedded=false`) it keeps the full Variant-A
// surface, including its own header "+ Add charge" button.
export default function RunningBillPanel({ admission, onDischarge, embedded = false, openAddChargeToken, onAdmissionPatched, diagnosticsRefreshToken }) {
  if (hasFinalBill(admission.status)) return <FinalBillView admission={admission} />;
  return <RunningBillLive admission={admission} onDischarge={onDischarge} embedded={embedded} openAddChargeToken={openAddChargeToken} onAdmissionPatched={onAdmissionPatched} diagnosticsRefreshToken={diagnosticsRefreshToken} />;
}

function RunningBillLive({ admission, onDischarge, embedded = false, openAddChargeToken, onAdmissionPatched, diagnosticsRefreshToken }) {
  const admissionId = admission.admission_id;
  // Authoritative denorm off the parent row — primitives so the external-change
  // reload effect below has stable, comparable deps (never an object/array).
  const balancePaise = admission.balance_paise;
  const depositTotalPaise = admission.deposit_total_paise;
  const canAddPerm = usePermission(CHARGE_PERMISSIONS.ADD_CHARGE);
  const canReverse = usePermission(CHARGE_PERMISSIONS.REVERSE_CHARGE);
  // S5 AC4 — admin bypasses the same-business-day reversal window entirely.
  const { isAdmin } = useScope();
  const canCollectPerm = usePermission(PAYMENT_PERMISSIONS.COLLECT_DEPOSIT);
  const canRefund = usePermission(PAYMENT_PERMISSIONS.REFUND_DEPOSIT);
  const canPrintReceipt = usePermission(PAYMENT_PERMISSIONS.GENERATE_RECEIPT_PDF);
  // S3 — provisional "Print bill" (§2.4 GENERATE_IPD_BILL_PDF, same slug the
  // locked FinalBillView's reprint reads — one perm, two surfaces, never a
  // second slug for the same document family).
  const canPrintInvoice = usePermission(DISCHARGE_PERMISSIONS.GENERATE_IPD_BILL_PDF);
  // L3 mount (ADDENDUM-3) — Settle Payment card gates on the existing
  // COLLECT_PAYMENT slug (the same perm the settle route itself enforces).
  const canSettleCredits = usePermission(PAYMENT_PERMISSIONS.COLLECT_PAYMENT);
  const canDischarge = usePermission(DISCHARGE_PERMISSIONS.DISCHARGE_PATIENT)
    && (isDischargeStartable(admission.status) || isInDischargeFlow(admission.status));
  // Waiver/concession (C4 defect fix) — bill-level, ADMIN only, same tier as
  // REFUND_DEPOSIT (segregation of duties); not a per-deposit-row action.
  const canGrantConcession = usePermission(CHARGE_PERMISSIONS.GRANT_CONCESSION);
  // Bed-charging switch-off (Round 3 Wave 3, S3/S4) — read-only selector off
  // the SAME org-settings store `useAdmissionEnabled`/the module gate already
  // populates before this panel ever mounts; never fetched again here
  // (single-flight owned by the store, no duplicate request). Undefined
  // (flag not yet wired into the store) resolves falsy — same "off" default
  // the backend ships (DECISIONS.md #1), so the manual-billing note is
  // correct even before that store field lands.
  const bedDayAccrualEnabled = useOrgSettingsStore((s) => s.bedDayAccrualEnabled);
  const terminal = isTerminal(admission.status);
  const canAdd = canAddPerm && !terminal;
  // Deposit collectable only on ADMITTED (incl. no-bed) AND with the perm.
  const canCollect = canCollectPerm && isDepositCollectable(admission.status);
  const readOnly = !canAddPerm && !canReverse && !canCollect && !canRefund && !canGrantConcession;

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // admission-access — the department wall refused this read: the shared panel, not an error box.
  const [outOfScope, setOutOfScope] = useState(false);
  const [filter, setFilter] = useState(CHARGE_FILTER_ALL);
  const [query, setQuery] = useState('');
  const [showOlder, setShowOlder] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind:'add' } | { kind:'reverse', charge }
  const fetchedRef = useRef(false);

  // Add-charge relocation (Round 3 Wave 3) — the workspace taskbar's "+ Add
  // charge" bumps `openAddChargeToken`; open this panel's OWN existing 'add'
  // dialog state off it (no second AddChargeDialog instance). Guarded so only
  // a real CHANGE opens it — the initial mount value must not auto-open.
  const openAddChargeTokenRef = useRef(openAddChargeToken);
  useEffect(() => {
    if (openAddChargeToken != null && openAddChargeToken !== openAddChargeTokenRef.current) {
      openAddChargeTokenRef.current = openAddChargeToken;
      setDialog({ kind: 'add' });
    }
  }, [openAddChargeToken]);

  const reload = useCallback(async () => {
    const res = await getRunningBill(admissionId);
    if (res.success) { setBill(res.data); setError(null); }
    else if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) setOutOfScope(true);
    else setError(res.error || 'Failed to load the running bill.');
    setLoading(false);
  }, [admissionId]);

  // Live parent-row patch from a money-mutation RESPONSE (no refetch). Threaded
  // in as `onAdmissionPatched` by the desk (its single-source
  // `patchAdmissionBalance` seam); this is what keeps the workspace KPI strip /
  // deposit-low banner / rail chip live. Standalone (Variant A, no callback) →
  // a no-op. patchAdmissionBalance null-guards each field, so a response missing
  // a field simply leaves that field untouched.
  //
  // The patched values flow back down as new `admission.balance_paise/…` props
  // and would trip the external-change reload effect below into a SECOND fetch
  // (the handler already reloaded). `ownPatchRef` records the RESULTING denorm
  // (patch value, else the current prop — same verbatim null-guarded store
  // patchAdmissionBalance does, so the values line up) so that effect can
  // recognise this panel's OWN patch and skip the redundant reload.
  const ownPatchRef = useRef(null);
  const patchRow = useCallback((patch) => {
    ownPatchRef.current = {
      balance: patch.balance_paise != null ? patch.balance_paise : balancePaise,
      deposit: patch.deposit_total_paise != null ? patch.deposit_total_paise : depositTotalPaise,
    };
    if (typeof onAdmissionPatched === 'function') {
      onAdmissionPatched({ admission_id: admissionId, ...patch });
    }
  }, [onAdmissionPatched, admissionId, balancePaise, depositTotalPaise]);

  // Single-flight: one fetch per mount (panel only mounts when the row expands).
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    reload();
  }, [reload]);

  // External-change in-place reload (replaces the old balance-driven REMOUNT).
  // When the authoritative denorm balance/deposit moves from OUTSIDE this panel
  // — the workspace taskbar's collect-deposit / return-advance, or the WS
  // ADMISSION_BALANCE_CHANGED patch — reload the ledger IN PLACE (updates `bill`
  // state, never remounts) so an open dialog inside the panel (AddChargeDialog
  // "Save & add another", reverse, refund, concession) survives the refresh.
  // C2 folds `diagnosticsRefreshToken` into the SAME effect rather than a
  // second one — a lab order changes `bill.has_diagnostics` (the Generate
  // Diagnostics bill enable flag), which only `reload()` can refresh, and
  // this is the one place that already debounces reload calls.
  // Bounded to EXACTLY one refetch per real external change:
  //   • seenRef is seeded with the mount values, so the initial run is a no-op
  //     (no double-fetch on top of the single-flight mount reload).
  //   • ownPatchRef skips a change THIS panel itself just produced — its handler
  //     already reloaded — so a panel action costs exactly one fetch, no flicker.
  const seenRef = useRef({ balance: balancePaise, deposit: depositTotalPaise, diagnosticsRefreshToken });
  useEffect(() => {
    const prev = seenRef.current;
    if (prev.balance === balancePaise && prev.deposit === depositTotalPaise && prev.diagnosticsRefreshToken === diagnosticsRefreshToken) return;
    seenRef.current = { balance: balancePaise, deposit: depositTotalPaise, diagnosticsRefreshToken };
    const own = ownPatchRef.current;
    if (own && own.balance === balancePaise && own.deposit === depositTotalPaise) {
      ownPatchRef.current = null; // our own patch — the handler already reloaded
      return;
    }
    reload();
  }, [balancePaise, depositTotalPaise, diagnosticsRefreshToken, reload]);

  const model = useMemo(
    () => buildBillModel({ charges: bill?.charges, filter, query, showOlder }),
    [bill, filter, query, showOlder],
  );
  // DIAG-DISCOUNT v5 FE-G — single-source recorded-waiver state, shared with
  // FinalBillView and the Diagnostics tab via the SAME selector.
  const diagWaiverLine = useMemo(() => buildDiagWaiverLine(bill), [bill]);
  // A1 · C1b (FE-5) — identity-only claim block off the SAME running-bill
  // payload already loaded (no extra fetch); single selector shared with the
  // final bill's identity fields via `buildInsuranceIdentity`.
  const runningInsurance = useMemo(() => buildRunningInsuranceModel(bill), [bill]);

  // S2 — item picker post (server-priced, never a client rate). The dialog
  // itself decides Save & add another (stays open) vs Save & close (calls
  // onClose separately) — this handler only posts + reloads on success.
  const handleAddItem = useCallback(async (body) => {
    const res = await addItemCharge(admissionId, body);
    if (res.success) { await reload(); patchRow({ balance_paise: res.data.balance_paise }); }
    return res;
  }, [admissionId, reload, patchRow]);

  // S2 AC4 — legacy free-text one-off charge, admin-only (server also gates
  // on MANAGE_CHARGE_MASTER; this route/body is byte-unchanged).
  const handleAddFreeText = useCallback(async (body) => {
    const res = await addCharge(admissionId, body);
    if (res.success) { await reload(); patchRow({ balance_paise: res.data.balance_paise }); }
    return res;
  }, [admissionId, reload, patchRow]);

  // S3 / R3-1 — open the provisional bill PDF (blob + in-app viewer, same
  // pattern as the receipt/voucher print already on this panel). This branch
  // (RunningBillLive) only ever mounts while the admission is OPEN, but a
  // resumed DISCHARGE_INITIATED/BILLING_CLEARANCE stay already has a MINTED
  // invoice number (R3-1 REVERSAL-1/2 fix #8a) — the label + passed title
  // must reflect that instead of always reading "Interim Bill", or the owner
  // clicks a button that says "Interim Bill" and gets a numbered Invoice.
  // Single source: `isInDischargeFlow(status) || invoice_number_printed`.
  const billLabel = (isInDischargeFlow(admission.status) || admission.invoice_number_printed) ? 'Invoice' : 'Interim Bill';

  // TPA-PREAUTH/tpa-bill S1 (FE-2) — "TPA Bill" is DISTINCT from the
  // Interim Bill/Invoice button above: it ISSUES a new numbered document
  // rather than printing the running total. Gated on the SAME
  // GENERATE_IPD_BILL_PDF slug (`canPrintInvoice`, already read above — no
  // second `usePermission` call) AND `canIssueTpaBill(status, payer_mode)`,
  // the SHARED predicate in constants/admission.js (integration reconcile
  // item (c)) — CASHLESS only, and hidden once the admission is terminal
  // (a new issue is 409 ISSUE_AFTER_COMPLETION by contract). `payerMode`
  // comes off the SAME `runningInsurance` selector the claim block already
  // uses (single source — never a second payer_mode read). Hidden entirely
  // for SELF-PAY/REIMBURSEMENT — never a 409-on-click.
  const { issuedTpaBills, issuing: issuingTpaBill, error: tpaBillError, toast: tpaBillToast, issue: handleIssueTpaBill } = useTpaBillIssue(admissionId);
  const showTpaBill = canPrintInvoice && canIssueTpaBill(admission.status, runningInsurance.payerMode);
  // FIX-16 — shown but DISABLED WITH THE REASON when the claim identity is
  // missing (never hidden: the desk must see why, and the fix is one card away).
  const tpaBillBlocked = tpaBillBlockedReason(admission);

  // bk-110 — the diagnostics-bill button is disabled when the stay carries no
  // diagnostics. `has_diagnostics` is a FROZEN field on the RUNNING-BILL
  // response (GET /admissions/{id}/running-bill → `bill`), NOT on the admission
  // row prop — reading it off the fetched `bill` is what makes the disable
  // actually fire. Fails OPEN: only an explicit `false` disables the button, so
  // a still-loading (`bill` null) or pre-integration `undefined` never blocks it.
  const diagnosticsBillDisabled = bill?.has_diagnostics === false;

  // bk-112 — both print actions surface their `{success:false, error}` on a
  // visible inline banner (same pattern as the deposit-low banner / BedPopover
  // toast elsewhere in this domain) instead of failing silently, and show a
  // per-button loading state while the blob download is in flight. Refs
  // single-flight-guard against a double-click firing a second request.
  const [printingInvoice, setPrintingInvoice] = useState(false);
  const [printError, setPrintError] = useState(null);
  const printingInvoiceRef = useRef(false);

  const handlePrintInvoice = useCallback(async () => {
    if (printingInvoiceRef.current) return;
    printingInvoiceRef.current = true;
    setPrintingInvoice(true);
    setPrintError(null);
    const res = await openInterimInvoicePdf(admissionId, billLabel);
    printingInvoiceRef.current = false;
    setPrintingInvoice(false);
    if (!res.success) setPrintError(res.error || 'Could not open the invoice.');
  }, [admissionId, billLabel]);

  // Wave-3 FE-2 / F1-F2 — the ONE shared print helper (confirm-before-mint,
  // printing flag, error mapping). Never a forked copy of this logic. Status
  // is read fresh at click time (`generate(admission.status)`), never bound
  // at mount.
  const {
    generate: generateLabBill,
    printing: printingLabBill,
    error: labBillError,
    confirmModal: labBillConfirmModal,
  } = useDiagnosticsBillPrint(admissionId);
  const handlePrintLabBill = useCallback(() => generateLabBill(admission.status), [generateLabBill, admission.status]);

  const handleReverse = useCallback(async ({ reason }) => {
    const res = await reverseCharge(admissionId, dialog?.charge?.charge_id, { reason });
    if (res.success || res.code === 'ALREADY_REVERSED') { setDialog(null); await reload(); }
    // Only a fresh reversal carries the post-write balance; an ALREADY_REVERSED
    // race (res.success === false) has no body — the reload above still refreshes.
    if (res.success) patchRow({ balance_paise: res.data.balance_paise });
    return res;
  }, [admissionId, dialog, reload, patchRow]);

  // Deposit collect + refund: the dialog stays OPEN on success to show the
  // receipt/voucher hand-off; we refresh the bill in the background so the
  // ledger + balance reflect the written row. The dialog owns its own
  // single-flight + idempotency key, so this cannot double-write.
  const handleCollectDeposit = useCallback(async (body) => {
    const res = await collectDeposit(admissionId, body);
    if (res.success) await reload();
    return res;
  }, [admissionId, reload]);

  const handleRefund = useCallback(async (body) => {
    const res = await refundDeposit(admissionId, dialog?.parent?.payment_id, body);
    if (res.success) {
      await reload();
      // Refund response has no deposit_total_paise_after — derive the new NET
      // held (held − refunded magnitude; `refund.amount_paise` is positive).
      // Balance is the server's authoritative `balance_paise_after`.
      const heldBefore = Number(admission.deposit_total_paise) || 0;
      patchRow({
        balance_paise: res.data.balance_paise_after,
        deposit_total_paise: heldBefore - res.data.refund.amount_paise,
        deposit_low: res.data.deposit_low,
      });
    }
    return res;
  }, [admissionId, dialog, reload, patchRow, admission.deposit_total_paise]);

  const handleConcession = useCallback(async (body) => {
    const res = await grantConcession(admissionId, body);
    if (res.success) { await reload(); patchRow({ balance_paise: res.data.balance_paise }); }
    return res;
  }, [admissionId, reload, patchRow]);

  const handlePrint = useCallback((paymentId, opts) => {
    openAdmissionReceiptPdf(admissionId, paymentId, opts);
  }, [admissionId]);

  // ── Loading: skeleton, NEVER a fake ₹0 ──────────────────────────────────
  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" data-testid="running-bill-loading">
        {[40, 85, 78, 82].map((w, i) => (
          <div key={i} className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse mb-2.5" style={{ width: `${w}%` }} />
        ))}
        <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-2">Loading the running bill…</div>
      </div>
    );
  }
  if (outOfScope) return <NoAccessPanel variant={NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT} />;
  if (error) {
    return <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3" data-testid="running-bill-error">{error}</div>;
  }
  if (!bill) return null;

  const los = daysAdmitted(admission);
  const awaiting = isAwaitingBed(admission);
  const ft = bill.frozen_tariff;
  const hasCharges = (bill.charges || []).length > 0;
  const balance = describeBalance(bill.balance_paise);
  const depositLow = !!bill.deposit_low;
  const shortfallPaise = Number(bill.deposit_shortfall_paise) || 0;
  // S4 (FE-4, Screen C) — read-only, absent (no empty chip) when unplanned
  // OR once the stay is discharged/terminal (D3). Single source off
  // `admission` (C2) — never re-derived.
  const plannedDischargeChip = plannedDischargeChipText(admission);

  // S4 (owner-locked DECISIONS.md #5) — soft, NON-BLOCKING leakage reminder:
  // admitted day-buckets (the SAME `daysAdmitted` LOS the header "Day N" chip
  // already reads — single source) vs BED_DAY charge lines actually posted
  // (straight off `bill.charges`, already fetched — no extra call). This
  // assumes the bed has been occupied since admission; the surface has no
  // "bed attached on" field to compute the precise occupied-day window, so a
  // bed attached mid-stay makes this an approximate/generic gap count rather
  // than an exact one — acceptable for a soft reminder per the build plan's
  // degrade-not-a-backend-call instruction.
  // A "bed charge" is EITHER an auto-accrued line (kind BED_DAY, flag-on legacy)
  // OR a manually-billed seeded bed-class item. With the switch-off (flag off,
  // DECISIONS.md #7) reception posts bed charges via the seeded ROOM_BED
  // charge-items — those ride the generic item-charge path (kind === 'ITEM')
  // and are identified by the deterministic seed id prefix `chi_bedday_`
  // (BE-4 §C2). Counting only BED_DAY here would make the reminder never
  // decrement as staff add manual charges (R20 contract reconcile).
  const bedChargeCount = (bill.charges || []).filter(
    (c) => c.kind === CHARGE_KIND.BED_DAY
      || (c.source_ref || '').startsWith('chi_bedday_'),
  ).length;
  const admittedDayCount = los != null ? los + 1 : 0;
  const bedChargeGapCount = (!terminal && !awaiting) ? Math.max(0, admittedDayCount - bedChargeCount) : 0;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden" data-testid="running-bill-panel">
      {/* Header (standalone only — in the Variant-B Bill tab the workspace's
          pinned KPI strip + patient header own this glance). */}
      {!embedded && (
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-bold text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</div>
              <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5">
                {admission.ip_number}
                {los != null && ` · ${los === 0 ? 'Admitted today' : `Day ${los + 1}`}`}
                {admission.primary_doctor_name ? ` · ${admission.primary_doctor_name}` : ''}
              </div>
              <div className="flex gap-1.5 flex-wrap mt-2">
                <span className={chipMuted}>Running bill</span>
                {admission.bed_label
                  ? <span className={chipTeal}>Bed {admission.bed_label}{admission.ward_name ? ` · ${admission.ward_name}` : ''}</span>
                  : awaiting ? <span className={chipAmber}>Awaiting bed</span> : null}
                {depositLow && (
                  <span className={chipAmber} data-testid="deposit-low-chip">⚠ Deposit low</span>
                )}
                {ft && (
                  <span className={chipTeal} data-testid="frozen-rate-chip">{formatPaise(ft.per_day_paise)}/day · rate locked at admission</span>
                )}
                {plannedDischargeChip && (
                  <span className={chipTeal} data-testid="planned-discharge-chip">{plannedDischargeChip}</span>
                )}
              </div>
            </div>
            {canDischarge && onDischarge && (
              <button
                type="button"
                onClick={onDischarge}
                className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12.5px] font-semibold"
                data-testid="running-bill-discharge-open"
              >
                {isInDischargeFlow(admission.status) ? 'Resume discharge' : 'Discharge…'}
              </button>
            )}
            <div className="ml-auto text-right min-w-[150px]">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{balance.label}</div>
              <div className={`text-2xl font-extrabold leading-tight ${balance.kind === 'advance' ? 'text-teal-700 dark:text-teal-300' : 'text-slate-900 dark:text-white'}`} data-testid="running-bill-balance">{balance.text}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                {formatPaise(bill.total_charges_paise)} charges − {formatPaise(bill.total_deposits_paise)} deposits held
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DEPOSIT_LOW amber banner strip (standalone only — the workspace KPI
          strip owns this banner + cure button in the Variant-B Bill tab). */}
      {!embedded && depositLow && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-[12.5px]" data-testid="deposit-low-banner">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 flex-none" aria-hidden="true" />
          <span className="flex-1">
            <b>Deposit low</b>
            {shortfallPaise > 0
              ? <> — the bill has outrun the deposit by <b className="tabular-nums">{formatPaise(shortfallPaise)}</b>.</>
              : null}
          </span>
          {canCollect && (
            <button type="button" onClick={() => setDialog({ kind: 'deposit' })} className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12px] font-semibold flex-none" data-testid="deposit-low-cure">+ Add deposit</button>
          )}
        </div>
      )}

      {/* S3 — manual bed-charging note (Round 3 Wave 3). Calm/informational,
          not a warning: bed-day charges no longer post automatically while
          `bed_day_accrual_enabled` is off for this org, so staff shouldn't
          wait for one to appear. Shown on both the standalone and embedded
          (Bill tab) surfaces — this is new safety info, not a duplicate of
          anything the workspace KPI strip already shows. */}
      {!bedDayAccrualEnabled && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 text-[12px]" data-testid="bed-charge-manual-note">
          <span>Room and bed charges are added manually — use “+ Add charge” for each day the bed is occupied.</span>
          <span
            className="text-[11px] text-blue-600 dark:text-blue-300 cursor-help flex-none"
            title="Bed charges no longer post automatically at midnight. Add each bed-day charge manually so nothing is missed on the bill."
            data-testid="bed-charge-manual-note-tooltip"
          >
            ⓘ
          </span>
        </div>
      )}

      {/* S4 (owner-locked DECISIONS.md #5) — soft, NON-BLOCKING leakage
          reminder. Warning tone (same amber convention as DEPOSIT_LOW above),
          but carries no disabling of Add charge / Discharge — purely
          informational with a shortcut into the SAME "+ Add charge" dialog.
          Gated on `!bedDayAccrualEnabled` to match the S3 note (QA money-safety):
          in a flag-ON org the nightly cron posts the bed-day, so a false
          reminder there would tempt staff to add a manual `chi_bedday_` line
          with a different idem key than the cron's `bedday:{adm}:{date}` →
          DOUBLE bed-day charge. The flag gate removes that trap. */}
      {!bedDayAccrualEnabled && bedChargeGapCount > 0 && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-[12.5px]" data-testid="bed-charge-gap-banner">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 flex-none" aria-hidden="true" />
          <span className="flex-1">
            <b>{bedChargeGapCount}</b> day{bedChargeGapCount === 1 ? '' : 's'} admitted with no bed charge yet — review and add if needed.
          </span>
          {canAdd && (
            <button type="button" onClick={() => setDialog({ kind: 'add' })} className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12px] font-semibold flex-none" data-testid="bed-charge-gap-add">+ Add charge</button>
          )}
        </div>
      )}

      {/* Accrual status strip (business non-negotiable; standalone only — the
          desk page already shows ONE page-level AccrualRunBanner in Variant B). */}
      {!embedded && (
        <div className="px-4 pt-3">
          <AccrualRunBanner />
        </div>
      )}

      {readOnly && (
        <div className="px-4 py-2 text-[12px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 mt-3" data-testid="running-bill-readonly">
          You have view-only access to this bill. Contact the billing desk for changes.
        </div>
      )}

      {/* Insurer / claim identity block, RUNNING view (A1 · C1b) — IDENTITY
          ONLY: insurer, TPA, policy, claim no. + "Approved so far" as
          INFORMATION, never a deduction. Absent entirely for payer_mode NONE
          (cash/self-pay); the "Approved so far" row itself is omitted at ₹0
          so an admission awaiting its first approval never reads "₹0
          approved" as a fact. No ladder line here — `balance_paise` above is
          untouched; the "Less: Insurer-approved" deduction stays a
          FINAL-VIEW-ONLY row (owner ruling A1) once the settlement mints at
          discharge-initiation. */}
      {runningInsurance.showClaimBlock && (
        <div className="mx-4 mt-3 rounded-lg border border-teal-400 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 p-3 text-[12.5px]" data-testid="running-insurance-claim-block">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-teal-800 dark:text-teal-300 mb-1.5">
            {runningInsurance.blockTitle}
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-0.5 text-slate-700 dark:text-slate-200">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Insurer</span><b>{runningInsurance.insurerName || '—'}</b></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">TPA</span><b>{runningInsurance.tpaName || '—'}</b></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Policy no.</span><b>{runningInsurance.policyNo || '—'}</b></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{runningInsurance.claimLabel}</span><b>{runningInsurance.claimNo || '—'}</b></div>
          </div>
          {runningInsurance.showApprovedAmount && (
            <div className="flex justify-between mt-1.5 pt-1.5 border-t border-teal-300 dark:border-teal-700 font-semibold text-slate-800 dark:text-slate-100" data-testid="insurer-approved-so-far">
              <span>Approved so far</span>
              <b className="tabular-nums">{formatPaise(runningInsurance.insurerApprovedPaise)}</b>
            </div>
          )}
        </div>
      )}

      {/* Toolbar: filters + search + add charge (pinned above the list) */}
      <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-slate-200 dark:border-slate-700 mt-3">
        {CHARGE_FILTER_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setFilter(c.key)}
            className={`text-[12px] font-semibold px-3 py-1 rounded-full border ${filter === c.key ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200'}`}
            data-testid={`ledger-filter-${c.key}`}
          >{c.label}</button>
        ))}
        {model.showSearch && (
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search charges…"
            className="flex-1 min-w-[140px] max-w-[240px] px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
            data-testid="ledger-search"
          />
        )}
        <span className="ml-auto flex items-center gap-2">
          {/* Waiver/concession is a bill-level (not bill-line) action — in the
              Variant-B Bill tab it lives on the workspace task bar, so it is
              suppressed here when embedded to avoid the duplication. */}
          {!embedded && canGrantConcession && (
            <button type="button" onClick={() => setDialog({ kind: 'concession' })} className="px-3 py-1.5 rounded-lg border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[12.5px] font-semibold" data-testid="grant-concession-open">Waiver / concession</button>
          )}
          {/* DIAG-DISCOUNT v5 (FE-B) — the RECORD dialog, peer action to
              "Waiver / concession" above: distinct words, deliberately never
              shortened — this one reduces DIAGNOSTICS, not the admission's
              room/treatment bill. Same authority tier (GRANT_CONCESSION —
              "same as the waiver" ruling), so it rides the SAME suppression
              rule when embedded. Self-contained widget: renders its own
              entry button (disabled-with-reason for non-admin/walk-in/closed
              stay — never hidden) and owns its own modal. */}
          {!embedded && (
            <DiagnosticWaiverDialog
              admission={admission}
              canRecord={canGrantConcession}
              recordedPaise={diagWaiverLine.recordedPaise}
              reversedPaise={diagWaiverLine.reversedPaise}
              appliedPaise={diagWaiverLine.appliedPaise}
              onRecorded={reload}
            />
          )}
          {canPrintInvoice && (
            <button
              type="button"
              onClick={handlePrintInvoice}
              disabled={printingInvoice}
              className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="running-bill-print-invoice"
              title={billLabel === 'Invoice' ? 'Print invoice' : 'Print interim bill'}
            >{printingInvoice ? 'Generating…' : billLabel}</button>
          )}
          {/* TPA-PREAUTH/tpa-bill S1 — DISTINCT from the button above: this
              ISSUES a numbered TPA bill (a new –n if anything changed since
              the last one, otherwise the same –n reopens — FIX-4), never a
              print of today's running total. Interim Bill/Invoice stays
              reachable for every cashless admission (AC1) — this is an
              ADDITIONAL action beside it, never a replacement. */}
          {showTpaBill && (
            <button
              type="button"
              onClick={handleIssueTpaBill}
              disabled={issuingTpaBill || !!tpaBillBlocked}
              className="px-3 py-1.5 rounded-lg border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[12.5px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="running-bill-tpa-bill"
              title={tpaBillBlocked || 'Issues a new TPA bill if anything changed, otherwise opens the last one'}
            >{issuingTpaBill ? 'Issuing…' : 'TPA Bill'}</button>
          )}
          {canPrintInvoice && (
            <button
              type="button"
              onClick={handlePrintLabBill}
              disabled={printingLabBill || diagnosticsBillDisabled}
              className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="running-bill-print-lab-bill"
              title={diagnosticsBillDisabled ? 'No diagnostics to bill' : 'Generate the consolidated diagnostics bill'}
            >{printingLabBill ? 'Generating…' : diagnosticsBillLabel(admission.status)}</button>
          )}
          {/* Relocated (Round 3 Wave 3): in embedded mode "+ Add charge" now
              lives on the workspace task bar (single home — no duplicate);
              standalone (Variant A) keeps its own header button. */}
          {!embedded && canAdd && (
            <button type="button" onClick={() => setDialog({ kind: 'add' })} className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12.5px] font-semibold" data-testid="add-charge-open">+ Add charge</button>
          )}
        </span>
      </div>

      {(printError || labBillError || tpaBillError) && (
        <div className="px-4 py-2 text-[12px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-b border-red-300 dark:border-red-700" data-testid="running-bill-print-error">
          {printError || labBillError || tpaBillError}
        </div>
      )}

      {/* FIX-4-FE — transient toast, same auto-dismiss idiom used elsewhere
          in this domain (AdmissionWorkspace's plan-discharge toast). */}
      {tpaBillToast && (
        <div className="mx-4 mt-3 px-3 py-1.5 rounded-lg bg-slate-800 dark:bg-slate-700 text-white text-[12px] font-medium inline-block" data-testid="tpa-bill-toast">
          {tpaBillToast}
        </div>
      )}

      {/* Issued-this-session confirmation — a same-session-only echo of the
          POST response (never a re-fetched/replayed list, AC-I2 "no reload").
          The full persisted history + per-row Reprint lives on
          `TpaBillSection` (Screen C, Details tab). */}
      {issuedTpaBills.length > 0 && (
        <div className="px-4 pb-2 flex flex-col gap-1" data-testid="running-bill-tpa-bills-issued">
          {issuedTpaBills.map((b) => (
            <div key={b.seq_no} className="text-[12px] text-teal-700 dark:text-teal-300" data-testid={`tpa-bill-issued-${b.seq_no}`}>
              Issued {b.bill_no_display} · {formatPaise(b.total_paise)}
            </div>
          ))}
        </div>
      )}

      {/* Ledger */}
      {!hasCharges ? (
        <div className="p-6 text-center" data-testid="running-bill-empty">
          <div className="text-2xl mb-1">🛏️</div>
          <div className="font-semibold text-slate-900 dark:text-white">No charges yet</div>
          <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-1">
            {awaiting
              ? 'Awaiting bed — bed charges start once a bed is assigned. Other charges (e.g. labour room) can be added any time.'
              : bedDayAccrualEnabled
                ? 'Bed-days post automatically at midnight. Other charges can be added any time.'
                : 'Bed charges are added manually — use “+ Add charge” for each day the bed is occupied. Other charges can be added any time.'}
          </div>
        </div>
      ) : model.matchedLineCount === 0 ? (
        <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="ledger-no-match">No charges match this filter.</div>
      ) : (
        <>
          <div className="flex gap-2.5 px-4 py-1.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <span className="flex-1">Charge</span>
            <span className="w-28 text-right hidden sm:block">Qty × Rate</span>
            <span className="w-12 text-right hidden sm:block">GST</span>
            <span className="w-24 text-right">Amount</span>
            <span className="w-7" />
          </div>
          {model.visibleGroups.map((g) => (
            <div key={g.dateKey} className="border-b border-slate-200 dark:border-slate-700" data-testid={`ledger-day-${g.dateKey}`}>
              <div className="flex items-baseline gap-2 px-4 pt-2.5 pb-1 text-[11.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {g.label || g.dateKey}
                <span className="ml-auto font-semibold normal-case tracking-normal text-slate-500 dark:text-slate-400">Day total {formatPaise(g.subtotalPaise)}</span>
              </div>
              {g.lines.map((line) => (
                <LineRow key={line.charge_id} line={line} canReverse={canReverse} isAdmin={isAdmin} onReverse={(c) => setDialog({ kind: 'reverse', charge: c })} />
              ))}
            </div>
          ))}
          {model.hiddenDayCount > 0 && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowOlder(true)} className="w-full text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="ledger-expander">
                Show {model.hiddenDayCount} earlier day{model.hiddenDayCount === 1 ? '' : 's'} · {formatPaise(model.hiddenTotalPaise)}
              </button>
            </div>
          )}
          {showOlder && !model.isFiltering && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowOlder(false)} className="w-full text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="ledger-collapse">
                Hide earlier days
              </button>
            </div>
          )}
        </>
      )}

      {/* Deposits + refunds section (S2/S5) — the money story in one place. */}
      <DepositsSection
        bill={bill}
        canCollect={canCollect && !embedded}
        canRefund={canRefund}
        canPrint={canPrintReceipt}
        onCollect={() => setDialog({ kind: 'deposit' })}
        onRefund={(parent) => setDialog({ kind: 'refund', parent })}
        onPrint={(row) => handlePrint(row.payment_id, { isRefund: row.isRefund })}
      />

      {/* DIAG-DISCOUNT v5 FE-G — the recorded-waiver line the owner was
          missing: it must be visible in the whole gap between an admin
          recording it and a receptionist settling it, not only after. */}
      <DiagWaiverBillLine line={diagWaiverLine} />

      {/* L3 mount (day-1 ADDENDUM-3) — this stay's outstanding lab-credit
          entries + batch-settle, built on the labcredit-fe lane at the
          imported path; Suspense fallback covers the lazy-chunk fetch only
          (the card owns its own single-flight GET /credits load). */}
      {canSettleCredits && (
        <Suspense fallback={null}>
          <SettleCreditsCard admissionId={admissionId} diagnosticsRefreshToken={diagnosticsRefreshToken} />
        </Suspense>
      )}

      {/* Totals */}
      <div className="p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
        <div className="flex justify-between text-[13px] py-1 text-slate-700 dark:text-slate-200">
          <span>Total charges ({bill.count} item{bill.count === 1 ? '' : 's'})</span>
          <span className="tabular-nums font-semibold text-slate-900 dark:text-white">{formatPaise(bill.total_charges_paise)}</span>
        </div>
        <div className="flex justify-between text-[13px] py-1 text-teal-700 dark:text-teal-300" data-testid="deposits-row">
          <span>Deposits held (net of refunds)</span>
          <span className="tabular-nums font-semibold">− {formatPaise(bill.total_deposits_paise)}</span>
        </div>
        <div className="flex justify-between items-center border-t border-slate-300 dark:border-slate-600 mt-1.5 pt-2.5 text-[15px] font-extrabold text-slate-900 dark:text-white">
          <span>{balance.label}</span>
          <span className={`tabular-nums text-[17px] ${balance.kind === 'advance' ? 'text-teal-700 dark:text-teal-300' : ''}`} data-testid="totals-balance">{balance.text}</span>
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
          No GST has been charged on this bill. Deposits are advances, not payment for a taxable supply.
        </div>
      </div>

      {dialog?.kind === 'add' && (
        <AddChargeDialog
          admission={admission}
          balancePaise={bill.balance_paise}
          frozenTariff={ft}
          onClose={() => setDialog(null)}
          onSubmitItem={handleAddItem}
          onSubmitFreeText={handleAddFreeText}
        />
      )}
      {dialog?.kind === 'reverse' && (
        <ReverseChargeDialog charge={dialog.charge} balancePaise={bill.balance_paise} onClose={() => setDialog(null)} onSubmit={handleReverse} />
      )}
      {dialog?.kind === 'deposit' && (
        <TakeDepositDialog
          admission={admission}
          balancePaise={bill.balance_paise}
          depositsHeldPaise={bill.total_deposits_paise}
          onClose={() => setDialog(null)}
          onSubmit={handleCollectDeposit}
          onPrintReceipt={canPrintReceipt ? handlePrint : undefined}
        />
      )}
      {dialog?.kind === 'refund' && (
        <RefundDialog
          admission={admission}
          parent={dialog.parent}
          balancePaise={bill.balance_paise}
          onClose={() => setDialog(null)}
          onSubmit={handleRefund}
          onPrintVoucher={canPrintReceipt ? handlePrint : undefined}
        />
      )}
      {dialog?.kind === 'concession' && (
        <WaiverConcessionDialog
          admission={admission}
          balancePaise={bill.balance_paise}
          netChargesPaise={bill.total_charges_paise}
          onClose={() => setDialog(null)}
          onSubmit={handleConcession}
        />
      )}
      {labBillConfirmModal}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FinalBillView (C5 · FE-3) — the LOCKED render of a discharged admission's
// bill. Reads GET /final-bill (contract #5, `admissionDischargeApi.getFinalBill`)
// instead of the live running-bill endpoint. Money CTAs (add charge / reverse /
// deposit / refund / concession) are ALL absent — this is a read + reprint
// surface only. Sections never cross: concessions stay a credit line under
// "Charges" (never the deposits column) and a return-of-advance stays an
// amber refund row under "Deposits & refunds" (never the charges column) —
// the frozen contract already keeps them in separate arrays; this view keeps
// them in separate SECTIONS on screen too.
// ═══════════════════════════════════════════════════════════════════════

const sectionHead = 'px-4 pt-3 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-t border-slate-200 dark:border-slate-700 flex items-baseline gap-2';
const chipGreen = `${chip} border-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200`;
const chipRed = `${chip} border-red-400 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200`;

function FinalBillView({ admission }) {
  const admissionId = admission.admission_id;
  const canPrintDoc = usePermission(DISCHARGE_PERMISSIONS.GENERATE_IPD_BILL_PDF);
  const canPrintReceipt = usePermission(PAYMENT_PERMISSIONS.GENERATE_RECEIPT_PDF);
  // Correction (#13/#14) is visibility-only here — no FE task in the C5
  // build-plan wires the open/close reason flow yet; gating the entry per
  // ADMIN-only slug is the FE-3 deliverable, not the correction dialog itself.
  const canCorrect = usePermission(DISCHARGE_PERMISSIONS.CORRECT_LOCKED_BILL);

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [filter, setFilter] = useState(CHARGE_FILTER_ALL);
  const [query, setQuery] = useState('');
  const [showOlder, setShowOlder] = useState(false);
  const fetchedRef = useRef(false);

  // Single-flight: one fetch per mount — a re-render can't multiply the request.
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      const res = await getFinalBill(admissionId);
      if (res.success) { setBill(res.data); setError(null); setErrorCode(null); }
      else { setError(res.error || 'Failed to load the final bill.'); setErrorCode(res.code || null); }
      setLoading(false);
    })();
  }, [admissionId]);

  const finalModel = useMemo(
    () => buildFinalBillModel({ bill, filter, query, showOlder }),
    [bill, filter, query, showOlder],
  );
  const { chargesModel, concessionLines, depositsAdapter, hasDues, duesPaise, balance, insurance } = finalModel;
  // TPA-PREAUTH/tpa-bill S1 (FE-2) — same shared issue hook as
  // RunningBillLive; gated on `canPrintDoc` (same GENERATE_IPD_BILL_PDF slug)
  // AND the SHARED `canIssueTpaBill(status, payer_mode)` predicate, with
  // `payerMode` off the SAME `insurance` selector the deduction/claim block
  // already uses (single source). Integration reconcile item (c) settled the
  // A2 question FE-2 flagged: a new issue is `409 ISSUE_AFTER_COMPLETION`
  // once the admission is terminal, so the ISSUE control is HIDDEN here
  // rather than 409-ing on click. Reprint of an already-issued bill is a
  // SEPARATE action on `TpaBillSection` and stays available forever.
  const { issuedTpaBills, issuing: issuingTpaBill, error: tpaBillError, toast: tpaBillToast, issue: handleIssueTpaBill } = useTpaBillIssue(admissionId);
  const showTpaBill = canPrintDoc && canIssueTpaBill(admission.status, insurance.payerMode);
  const tpaBillBlocked = tpaBillBlockedReason(admission);
  // Dues settlement addendum (C6b · FE-6) — READ-ONLY: no collect/write-off
  // affordance here (owner ruling 2). Purely derived from the bill's own
  // fields via the single-source selector; not recomputed anywhere else.
  const duesAddendum = useMemo(() => buildDuesAddendumModel(bill), [bill]);
  // DIAG-DISCOUNT v5 FE-G — SAME selector as the running bill, so a waiver
  // that lapsed or was left partly-unused stays visible on the final bill too.
  const diagWaiverLine = useMemo(() => buildDiagWaiverLine(bill), [bill]);

  const handlePrint = useCallback((paymentId, opts) => {
    openAdmissionReceiptPdf(admissionId, paymentId, opts);
  }, [admissionId]);
  // D1: reprint the unified Invoice at its FINAL state (blob + in-app viewer),
  // not the retired final-bill PDF.
  // bk-112 — same silent-failure fix as the running-bill invoice button: a
  // per-click loading state + visible error surface, single-flight guarded.
  const [reprinting, setReprinting] = useState(false);
  const [printError, setPrintError] = useState(null);
  const reprintingRef = useRef(false);
  const handleReprintInvoice = useCallback(async () => {
    if (reprintingRef.current) return;
    reprintingRef.current = true;
    setReprinting(true);
    setPrintError(null);
    const res = await openInterimInvoicePdf(admissionId);
    reprintingRef.current = false;
    setReprinting(false);
    if (!res.success) setPrintError(res.error || 'Could not open the invoice.');
  }, [admissionId]);

  // FIX-7-FE — discharge/death summary opens in the app's own viewer
  // (Print/Close chrome), never a new tab. Same single-flight guard + shared
  // `printError` banner as the reprint-invoice button above (one error slot
  // for every print action in this strip).
  const [printingSummary, setPrintingSummary] = useState(false);
  const printingSummaryRef = useRef(false);
  const handlePrintDischargeSummary = useCallback(async () => {
    if (printingSummaryRef.current) return;
    printingSummaryRef.current = true;
    setPrintingSummary(true);
    setPrintError(null);
    const title = isDeathOutcome(admission.status) ? 'Death summary' : 'Discharge summary';
    const res = await openDischargeSummaryPdf(admissionId, { title });
    printingSummaryRef.current = false;
    setPrintingSummary(false);
    if (!res.success) setPrintError(res.error || 'Could not open the discharge summary.');
  }, [admissionId, admission.status]);

  // F3 — post-terminal reachability: a stay that has already ended (this
  // component only mounts for DISCHARGED/DECEASED, `hasFinalBill`) still
  // needs a route to a numbered diagnostics bill, most of all a DECEASED
  // stay — every other route (the IPD tile, the discharge dialog) is gone
  // once the stay ends. Same shared print helper as F1/F2 — no forked copy.
  // `admission.status` is reliable here: mounting THIS component already
  // proves it's DISCHARGED/DECEASED, both invoice-bearing (D-4), so the
  // confirm always applies.
  const {
    generate: generateLabBill,
    printing: printingLabBill,
    error: labBillError,
    confirmModal: labBillConfirmModal,
  } = useDiagnosticsBillPrint(admissionId);
  const handlePrintLabBill = useCallback(() => generateLabBill(admission.status), [generateLabBill, admission.status]);
  // `has_diagnostics` (B6, additive on GET /final-bill) — never an
  // always-enabled button over a would-be 404 (bk-625's exact mistake).
  const diagnosticsBillDisabled = bill?.has_diagnostics === false;

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" data-testid="final-bill-loading">
        {[40, 85, 78, 82].map((w, i) => (
          <div key={i} className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse mb-2.5" style={{ width: `${w}%` }} />
        ))}
        <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-2">Preparing the bill…</div>
      </div>
    );
  }
  if (error) {
    if (errorCode === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) return <NoAccessPanel variant={NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT} />;
    const text = errorCode === DISCHARGE_ERROR_CODE.LEDGER_DRIFT
      ? 'The bill could not be prepared because the figures did not match on re-check. Nothing was printed. Please contact your admin.'
      : error;
    return <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3" data-testid="final-bill-error">{text}</div>;
  }
  if (!bill) return null;

  const los = daysAdmitted({ admitted_at: bill.header?.admitted_at }, bill.discharged_at ? new Date(bill.discharged_at) : new Date());
  const hasCharges = (bill.charges || []).length > 0;
  const hasConcessions = concessionLines.length > 0;
  const amended = Number(bill.bill_rev) > 1;
  // Final bill nets out fully-reversed pairs server-side (bk-579): a
  // REVERSAL row can never appear in bill.charges here, so the Reversals
  // chip would always yield an empty list. The running bill (above) is the
  // audit trail and keeps both chips.
  const chargeFilterChips = CHARGE_FILTER_CHIPS.filter(
    (c) => c.key !== CHARGE_KIND.CONCESSION && c.key !== CHARGE_KIND.REVERSAL,
  );
  // S4 (FE-4, Screen C) — same read-only selector as the running view.
  // FinalBillView renders only once the stay is discharged/terminal, so
  // `plannedDischargeChipVisible` (D3) always hides this chip here — a plan
  // that was never the DOD must not show on a discharged patient's bill.
  const plannedDischargeChip = plannedDischargeChipText(admission);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden" data-testid="final-bill-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-bold text-slate-900 dark:text-white">{bill.patient_name || admission.patient_name}</div>
            <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5">
              {bill.ip_number}
              {bill.header?.admitted_at && ` · Admitted ${formatIsoDateOnly(bill.header.admitted_at)}`}
              {bill.discharged_at && ` → Discharged ${formatIsoDateOnly(bill.discharged_at)}`}
              {los != null && ` · ${los === 0 ? '1 day' : `${los + 1} days`}`}
              {bill.header?.treating_doctor ? ` · ${bill.header.treating_doctor}` : ''}
            </div>
            {(bill.header?.department_name || bill.header?.ward_name || bill.header?.bed_label || bill.header?.payer_type) && (
              <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
                {[
                  bill.header?.department_name,
                  bill.header?.ward_name && bill.header?.bed_label ? `${bill.header.ward_name} · Bed ${bill.header.bed_label}` : (bill.header?.ward_name || bill.header?.bed_label),
                  bill.header?.payer_type && (PAYER_TYPE_LABELS[bill.header.payer_type] || bill.header.payer_type),
                ].filter(Boolean).join(' · ')}
              </div>
            )}
            {bill.bill_number_printed && (
              <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5" data-testid="final-bill-number">
                Bill no. {bill.bill_number_printed} · Bill date {formatIsoDateOnly(bill.bill_issued_at)}
              </div>
            )}
            <div className="flex gap-1.5 flex-wrap mt-2">
              <span className={chipMuted}>Final bill</span>
              {bill.discharge_type && <span className={chipTeal}>{DISCHARGE_TYPE_LABELS[bill.discharge_type] || bill.discharge_type}</span>}
              {hasDues ? (
                <span className={chipRed} data-testid="dues-badge">Dues recorded · Unpaid {formatPaise(duesPaise)}</span>
              ) : balance.kind === 'zero' ? (
                <span className={chipGreen} data-testid="settled-badge">✓ Settled — balance ₹0</span>
              ) : null}
              {amended && (
                <span className={chipAmber} data-testid="amended-badge">AMENDED (rev {bill.bill_rev})</span>
              )}
              {plannedDischargeChip && (
                <span className={chipTeal} data-testid="planned-discharge-chip">{plannedDischargeChip}</span>
              )}
            </div>
            {amended && bill.bill_amended_at && (
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1" data-testid="amended-line">
                Amended on {formatIsoDateOnly(bill.bill_amended_at)}{bill.bill_amendment_reason ? ` — ${bill.bill_amendment_reason}` : ''}
              </div>
            )}
          </div>
          <div className="ml-auto text-right min-w-[150px]">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{hasDues ? 'Dues recorded' : balance.label}</div>
            <div className={`text-2xl font-extrabold leading-tight ${hasDues ? 'text-red-600 dark:text-red-400' : balance.kind === 'advance' ? 'text-teal-700 dark:text-teal-300' : 'text-slate-900 dark:text-white'}`} data-testid="final-bill-balance">
              {hasDues ? formatPaise(duesPaise) : balance.text}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              {formatPaise(bill.net_charges_paise)} charges − {formatPaise(bill.net_deposits_paise)} deposits held
            </div>
          </div>
        </div>
      </div>

      {/* Locked strip */}
      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-[12.5px] flex-wrap" data-testid="final-bill-locked-strip">
        <span aria-hidden="true">🔒</span>
        <span className="flex-1 min-w-[220px]">This bill is final. Reprints keep the same bill number, and every copy generated after the first is marked as a reprint.</span>
        {canCorrect ? (
          <span className="text-[12px] font-semibold text-teal-700 dark:text-teal-300" data-testid="correct-bill-entry">Correct bill (admin)</span>
        ) : (
          <span className="text-[11.5px] text-slate-500 dark:text-slate-400" data-testid="correct-bill-disabled">Only an admin can correct a final bill.</span>
        )}
        {canPrintDoc && (
          <span className="flex items-center gap-2 flex-wrap ml-auto">
            <button type="button" onClick={handlePrintDischargeSummary} disabled={printingSummary} className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed" data-testid="print-discharge-summary">{printingSummary ? 'Opening…' : (isDeathOutcome(admission.status) ? 'Print death summary' : 'Print discharge summary')}</button>
            <button type="button" onClick={handleReprintInvoice} disabled={reprinting} className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed" data-testid="reprint-invoice">{reprinting ? 'Generating…' : 'Print invoice'}</button>
            {/* TPA-PREAUTH/tpa-bill S1 — DISTINCT from "Print invoice" above:
                issues a numbered TPA bill (a new –n if anything changed
                since the last one, otherwise the same –n reopens — FIX-4).
                Gated on the shared `canIssueTpaBill` predicate, which hides
                it once the admission is terminal — the state this view
                mounts in today — so the desk never clicks into a guaranteed
                `409 ISSUE_AFTER_COMPLETION`. The wiring stays here (rather
                than deleting the block) because the predicate is the single
                source for the rule in BOTH bill views. Per-row Reprint of an
                already-issued bill lives on `TpaBillSection` (Screen C) and
                is unaffected. */}
            {showTpaBill && (
              <button
                type="button"
                onClick={handleIssueTpaBill}
                disabled={issuingTpaBill || !!tpaBillBlocked}
                className="px-3 py-1.5 rounded-lg border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[12px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="final-bill-tpa-bill"
                title={tpaBillBlocked || 'Issues a new TPA bill if anything changed, otherwise opens the last one'}
              >{issuingTpaBill ? 'Issuing…' : 'TPA Bill'}</button>
            )}
            {/* F3 — post-terminal reachability (the button this whole task
                exists for): a stay that has already ended, most of all a
                DECEASED one, can still be walked to a numbered diagnostics
                bill without re-opening the discharge dialog. */}
            <button
              type="button"
              onClick={handlePrintLabBill}
              disabled={printingLabBill || diagnosticsBillDisabled}
              className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="final-bill-print-lab-bill"
              title={diagnosticsBillDisabled ? 'No diagnostics to bill' : 'Generate the consolidated diagnostics bill'}
            >{printingLabBill ? 'Generating…' : diagnosticsBillLabel(admission.status)}</button>
          </span>
        )}
      </div>

      {(printError || labBillError || tpaBillError) && (
        <div className="px-4 py-2 text-[12px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-b border-red-300 dark:border-red-700" data-testid="final-bill-print-error">
          {printError || labBillError || tpaBillError}
        </div>
      )}

      {/* FIX-4-FE — same transient toast idiom as RunningBillLive's. */}
      {tpaBillToast && (
        <div className="mx-4 mt-3 px-3 py-1.5 rounded-lg bg-slate-800 dark:bg-slate-700 text-white text-[12px] font-medium inline-block" data-testid="tpa-bill-toast">
          {tpaBillToast}
        </div>
      )}

      {/* Issued-this-session confirmation — same idea as RunningBillLive's:
          a same-session echo of the POST response, never a reload/refetch.
          The persisted history + per-row Reprint lives on `TpaBillSection`. */}
      {issuedTpaBills.length > 0 && (
        <div className="px-4 pb-2 flex flex-col gap-1" data-testid="final-bill-tpa-bills-issued">
          {issuedTpaBills.map((b) => (
            <div key={b.seq_no} className="text-[12px] text-teal-700 dark:text-teal-300" data-testid={`tpa-bill-issued-${b.seq_no}`}>
              Issued {b.bill_no_display} · {formatPaise(b.total_paise)}
            </div>
          ))}
        </div>
      )}

      {/* Insurer / claim identity block (FE-2 · C-2) — cashless AND
          reimbursement both surface insurer/policy/claim/TPA; cash (payer_mode
          NONE) renders nothing here. Never shown for cash — the on-screen bill
          matches the PDF renderer's (BE-5) identity block. */}
      {insurance.showClaimBlock && (
        <div className="mx-4 mt-3 rounded-lg border border-teal-400 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 p-3 text-[12.5px]" data-testid="insurance-claim-block">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-teal-800 dark:text-teal-300 mb-1.5">
            {insurance.blockTitle}
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-0.5 text-slate-700 dark:text-slate-200">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Insurer</span><b>{insurance.insurerName || '—'}</b></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">TPA</span><b>{insurance.tpaName || '—'}</b></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Policy no.</span><b>{insurance.policyNo || '—'}</b></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{insurance.claimLabel}</span><b>{insurance.claimNo || '—'}</b></div>
          </div>
          {insurance.reimbursementNote && (
            <div className="text-[11px] italic text-slate-500 dark:text-slate-400 mt-2" data-testid="reimbursement-note">
              {insurance.reimbursementNote}
            </div>
          )}
        </div>
      )}

      {/* Charges */}
      <div className={sectionHead.replace('border-t border-slate-200 dark:border-slate-700', '')}>Charges</div>
      <div className="flex items-center gap-2 flex-wrap px-4 pb-3 border-b border-slate-200 dark:border-slate-700">
        {chargeFilterChips.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setFilter(c.key)}
            className={`text-[12px] font-semibold px-3 py-1 rounded-full border ${filter === c.key ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200'}`}
            data-testid={`final-bill-filter-${c.key}`}
          >{c.label}</button>
        ))}
        {chargesModel.showSearch && (
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search bill lines"
            className="flex-1 min-w-[140px] max-w-[240px] px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
            data-testid="final-bill-search"
          />
        )}
      </div>
      {!hasCharges ? (
        <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="final-bill-empty">No charges were posted for this admission.</div>
      ) : chargesModel.matchedLineCount === 0 ? (
        <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="final-bill-no-match">No charges match this filter.</div>
      ) : (
        <>
          {chargesModel.visibleGroups.map((g) => (
            <div key={g.dateKey} className="border-b border-slate-200 dark:border-slate-700" data-testid={`final-bill-day-${g.dateKey}`}>
              <div className="flex items-baseline gap-2 px-4 pt-2.5 pb-1 text-[11.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {g.label || g.dateKey}
                <span className="ml-auto font-semibold normal-case tracking-normal text-slate-500 dark:text-slate-400">Day total {formatPaise(g.subtotalPaise)}</span>
              </div>
              {g.lines.map((line) => (
                <LineRow key={line.charge_id} line={line} canReverse={false} onReverse={() => {}} />
              ))}
            </div>
          ))}
          {chargesModel.hiddenDayCount > 0 && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowOlder(true)} className="w-full text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="final-bill-expander">
                Show {chargesModel.hiddenDayCount} earlier day{chargesModel.hiddenDayCount === 1 ? '' : 's'} · {formatPaise(chargesModel.hiddenTotalPaise)}
              </button>
            </div>
          )}
          {showOlder && !chargesModel.isFiltering && (
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setShowOlder(false)} className="w-full text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="final-bill-collapse">
                Hide earlier days
              </button>
            </div>
          )}
        </>
      )}
      <div className="flex justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-[13px] text-slate-700 dark:text-slate-200">
        <span>Total charges</span>
        <span className="tabular-nums font-semibold text-slate-900 dark:text-white">{formatPaise(bill.gross_charges_paise)}</span>
      </div>

      {/* Less: Concessions / waivers */}
      {hasConcessions && (
        <>
          <div className={sectionHead}>Less: Concessions / waivers <span className="ml-auto font-semibold normal-case tracking-normal text-teal-700 dark:text-teal-300">− {formatPaise(bill.concession_total_paise)}</span></div>
          {concessionLines.map((line) => (
            <LineRow key={line.charge_id} line={line} canReverse={false} onReverse={() => {}} />
          ))}
          <div className="flex justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-[13px] text-slate-700 dark:text-slate-200">
            <span>Net charges</span>
            <span className="tabular-nums font-semibold text-slate-900 dark:text-white">{formatPaise(bill.net_charges_paise)}</span>
          </div>
        </>
      )}

      {/* DIAG-DISCOUNT v5 FE-G — SAME line as the running bill; a stay that
          discharged with a partly-unused or never-settled waiver still shows
          it here, never silently drops it. */}
      <DiagWaiverBillLine line={diagWaiverLine} />

      {/* Deposits & refunds — reuses the C4 section (read-only: no collect/refund CTAs). */}
      <DepositsSection
        bill={depositsAdapter}
        canCollect={false}
        canRefund={false}
        canPrint={canPrintReceipt}
        onCollect={() => {}}
        onRefund={() => {}}
        onPrint={(row) => handlePrint(row.payment_id, { isRefund: row.isRefund })}
      />

      {/* Settlement */}
      <div className="p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
        {/* Insurer-approved (TPA) deduction — cashless only, peer of the
            "Deposits held (net)" line above; a display-only settlement row,
            never subtracted a second time (`balance_paise` already nets it
            server-side, INV-1/INV-2). Co-pay + disallowed render as ONE
            reason note, never their own deduction row (no double-count). */}
        {insurance.showInsurerDeduction && (
          <>
            <div className="flex justify-between text-[13px] py-1 text-teal-700 dark:text-teal-300" data-testid="insurer-deduction-row">
              <span>Less: Insurer-approved ({insurance.tpaName || insurance.insurerName || 'TPA'})</span>
              <span className="tabular-nums font-semibold">− {formatPaise(insurance.insurerSettledPaise)}</span>
            </div>
            {insurance.reasonLines.length > 0 && (
              <div className="pb-1" data-testid="insurer-reason-note">
                {/* Two lines mirror the PDF (BE-5): co-pay + disallowed
                    shown separately. */}
                {insurance.reasonLines.map((line, i) => (
                  <div key={i} className="text-[11px] italic text-slate-500 dark:text-slate-400">{line}</div>
                ))}
              </div>
            )}
          </>
        )}
        {hasDues ? (
          <div className="flex justify-between items-center text-[15px] font-extrabold text-slate-900 dark:text-white">
            <span>Dues recorded <span className={`${chipRed} ml-2 align-middle`}>Unpaid</span></span>
            <span className="tabular-nums text-[17px] text-red-600 dark:text-red-400" data-testid="final-bill-closing-balance">{formatPaise(duesPaise)}</span>
          </div>
        ) : (
          <>
            {balance.kind !== 'zero' && (
              <div className="flex justify-between text-[13px] py-1 text-slate-700 dark:text-slate-200">
                <span>{balance.kind === 'advance' ? 'Net refundable' : 'Net payable'}</span>
                <span className="tabular-nums font-semibold">{balance.text}</span>
              </div>
            )}
            <div className="flex justify-between items-center border-t border-slate-300 dark:border-slate-600 mt-1.5 pt-2.5 text-[15px] font-extrabold text-slate-900 dark:text-white">
              <span>Closing balance {balance.kind === 'zero' && <span className={`${chipGreen} ml-2 align-middle`}>✓ Settled</span>}</span>
              <span className={`tabular-nums text-[17px] ${balance.kind === 'advance' ? 'text-teal-700 dark:text-teal-300' : ''}`} data-testid="final-bill-closing-balance">{balance.text}</span>
            </div>
          </>
        )}
        {/* INV-3-FE: the banned in-app GST disclaimer ("No GST has been
            charged… This is not a tax invoice") has been removed. The
            statutory GST footer is data-driven and owned by the invoice PDF
            renderer (BE INV-3 lane), not free-handed in the app. */}
      </div>

      {/* Dues settlement addendum (C6b · FE-6, copy-deck §5.2) — READ-ONLY:
          collections/write-off history rendered under the unchanged bill.
          NO collect/write-off button anywhere in this block (owner ruling 2 —
          the final-bill panel never gets a collect shortcut; those actions
          live only on the Payment Pending queue row + admission detail). */}
      {duesAddendum.visible && (
        <div className="p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700" data-testid="dues-addendum">
          <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">{duesAddendum.heading}</h4>
          <div className="text-[12.5px] text-slate-600 dark:text-slate-300 mb-2" data-testid="dues-addendum-intro">{duesAddendum.introLine}</div>
          {duesAddendum.collectionLines.map((c) => (
            <div key={c.key} className="text-[12.5px] text-slate-700 dark:text-slate-200 py-1" data-testid="dues-addendum-collection">{c.text}</div>
          ))}
          {duesAddendum.writeOffLine && (
            <div className="text-[12.5px] text-slate-700 dark:text-slate-200 py-1" data-testid="dues-addendum-writeoff">{duesAddendum.writeOffLine}</div>
          )}
          <div className="text-[13px] font-semibold text-slate-900 dark:text-white mt-2 pt-2 border-t border-slate-200 dark:border-slate-700" data-testid="dues-addendum-closing">{duesAddendum.closingLine}</div>
        </div>
      )}
      {labBillConfirmModal}
    </div>
  );
}
